import React, { useCallback } from 'react'
import CancelOrder from './order/cancel'
import CancelInvoiceOrderModal from '../../Elements/modal/order/CancelInvoiceOrderModal'
import ChangePaymentConfirmModal from '../../Elements/modal/order/ChangePaymentConfirmModal'
import { isInvoiceOrder } from '../../../hooks/useCancellationWindow/helper'
import { useDispatch, useSelector } from 'react-redux'
import CustomerOrderButtonsModal from './order/'
import OrderDataModal from '../../Elements/modal/order/OrderDataModal'
import SingleDriverModal from '../../DriverMonitor/modal/singleDriver'
import { setModalOrderCustomer } from '../../../redux/action/customer'
import ModalPayment from '../../Elements/cart/ModalPayment'
import { confirmOrderPaymentsThunk, updateOrderPaymentsThunk } from '../../../redux/thunk/order'
import { setDeliveryCartOrder } from '../../../redux/action/deliveryCart'
import PrintCanceledOrder from './order/printCanceled'
import DriverConfirmationModal from '../../WoltDriver/DriverConfirmationModal'
import NoDriverModal from '../../WoltDriver/NoDriverModal'
import PrintReceiptConfirmationModal from '../../Elements/modal/PrintReceiptConfirmationModal'
import { roundPrice } from '../../../helper'
import { getGiftCouponsPayment, getGiftCouponsSum } from '../../../helper/getGiftCouponsPayment'
import { getPrepaidPayments, getPrepaidSum } from '../../../helper/getPrepaidPayments'
import { updateOrderCustomerThunk } from '../../../redux/thunk/order/updateOrderCustomer'
import PayerDataModal from '../../Elements/modal/PayerDataModal'
import { setSelectedOrder } from '../../../redux/action/order'
import { selectUsername } from '../../../redux/selector/system'
import AbortOrderModal from '../../Elements/modal/order/AbortOrderModal'
import { setOrderStatusThunk } from '../../../redux/thunk/order/setOrderStatus'

const DeliveryModals = React.memo(() => {
	const showOrderData = useSelector(store => store.customerStore.modal.showOrderData)
	const showPaymentMethod = useSelector(store => store.customerStore.modal.showPaymentMethod)
	const showReceiptPayerData = useSelector(store => store.customerStore.modal.showReceiptPayerData)
	const deliveryCartOrder = useSelector(store => store.deliveryCart.order)
	const orderData = useSelector(store => store.order.selectedOrder)
	const showDriverConfirmationModal = useSelector(store => store.woltDriverIntegrations.deliveryPromises)
	const showNoDriverModal = useSelector(store => store.woltDriverIntegrations.driverNotFound)
	const username = useSelector(selectUsername)
	const dispatch = useDispatch()
	const qrCodePaymentSession = useSelector(state => state.cartPayment.qrCodePaymentSession)
	const paymentSessionLink = qrCodePaymentSession?.link

	const orderPayment = deliveryCartOrder?.payments?.filter(item => item?.method === deliveryCartOrder?.paymentMethod)
	const isPaid = orderPayment?.[0]?.payed
	const totalSum = (isPaid && deliveryCartOrder?.tips) ? roundPrice(deliveryCartOrder?.totalPrice - deliveryCartOrder?.tips) : deliveryCartOrder?.totalPrice

	const prepaidPayments = getPrepaidPayments(deliveryCartOrder)
	const totalSumWithoutPrepaid = roundPrice(totalSum - getPrepaidSum(deliveryCartOrder))
	const showAbortOrder = useSelector(store => store.customerStore.modal.showAbortOrder)
	const showCancelOrder = useSelector(store => store.customerStore.modal.showCancelOrder)
	const showChangePaymentConfirm = useSelector(store => store.customerStore.modal.showChangePaymentConfirm)

	const closeModalPayment = useCallback((forceClose = true) => {
		// force = false: not reset order when pay by ec-terminal
		forceClose && dispatch(setDeliveryCartOrder(null))
		dispatch(setModalOrderCustomer({ showPaymentMethod: false }))
	}, [dispatch])

	const processUnpaidOrder = useCallback(async (paymentId, paymentMethod, giftCouponsAsPayment, tips, customer) => {
		const isEcTerminalPayment = paymentMethod === 'EC_TERMINAL'
		const isGiftCouponPayment = paymentMethod === 'GIFT_COUPON'
		const isQrCodePayment = paymentMethod === 'QR_CODE'
		const isInvoicePayment = paymentMethod === 'INVOICE'
		const isTap2PayPayment = paymentMethod === 'TAP_TO_PAY'
		if (isInvoicePayment) {
			await dispatch(updateOrderCustomerThunk(deliveryCartOrder.orderId, customer))
		}
		const paymentWithCoupons = getGiftCouponsPayment(giftCouponsAsPayment)
		const resentPayments = [...paymentWithCoupons, ...prepaidPayments]
		const giftCouponSum = getGiftCouponsSum(giftCouponsAsPayment)
		const orderPaymentId = (isEcTerminalPayment || isQrCodePayment || isTap2PayPayment) ? paymentId : orderPayment[0].id
		const paymentLink = isQrCodePayment ? paymentSessionLink : null
		const payments = isGiftCouponPayment ? resentPayments : [{ id: orderPaymentId, method: paymentMethod, total: roundPrice(totalSumWithoutPrepaid - giftCouponSum), link: paymentLink }, ...resentPayments]
		dispatch(confirmOrderPaymentsThunk(deliveryCartOrder.orderId, { payments, tips, printerMetaData: { userPrintRequest: { userName: username } } }, { module: deliveryCartOrder?.module?.moduleType, businessScope: deliveryCartOrder?.orderType, platformType: deliveryCartOrder?.platformMetaData?.platformType }))
	}, [prepaidPayments, orderPayment, deliveryCartOrder, dispatch, totalSumWithoutPrepaid, paymentSessionLink, username])

	const handlePayment = useCallback(async ({ paymentId, paymentMethod, tips = 0, giftCoupons: giftCouponsAsPayment, customer }) => {
		const isEcTerminalPaymentMethod = paymentMethod === 'EC_TERMINAL'
		const isQrCodePayment = paymentMethod === 'QR_CODE'
		const isTap2PayPaymentMethod = paymentMethod === 'TAP_TO_PAY'
		const isPaymentMethodWithTips = isEcTerminalPaymentMethod || isTap2PayPaymentMethod
		const resultTotalPrice = isPaymentMethodWithTips ? roundPrice(deliveryCartOrder.totalPrice - deliveryCartOrder.tips + tips) : roundPrice(deliveryCartOrder.totalPrice - deliveryCartOrder.tips)
		if (isPaid) {
			if (paymentMethod === 'INVOICE') {
				const isCustomerSaved = await dispatch(updateOrderCustomerThunk(deliveryCartOrder.orderId, customer))
				if (!isCustomerSaved) {
					return
				}
			}
			const orderPaymentId = (isEcTerminalPaymentMethod || isQrCodePayment || isTap2PayPaymentMethod) ? paymentId : orderPayment[0].id
			const paymentLink = isQrCodePayment ? paymentSessionLink : null
			const paymentData = {
				tips,
				payments: [{ id: orderPaymentId, method: paymentMethod, total: resultTotalPrice, deferred: false, link: paymentLink }],
				printerMetaData: { userPrintRequest: { userName: username } },
			}
			const printData = { module: deliveryCartOrder?.module?.moduleType, businessScope: deliveryCartOrder?.orderType, platformType: deliveryCartOrder?.platformMetaData?.platformType }
			dispatch(updateOrderPaymentsThunk(deliveryCartOrder.orderId, paymentData, printData))
		} else {
			processUnpaidOrder(paymentId, paymentMethod, giftCouponsAsPayment, tips, customer)
		}
		if (!isEcTerminalPaymentMethod && !isQrCodePayment && !isTap2PayPaymentMethod) {
			closeModalPayment()
		}
	}, [deliveryCartOrder, closeModalPayment, dispatch, isPaid, processUnpaidOrder, username, orderPayment, paymentSessionLink])

	const closeModalOrderData = useCallback(() => {
		dispatch(setModalOrderCustomer({ showOrderData: false }))
	}, [dispatch])

	const reOpenPaymentModal = useCallback(() => {
		dispatch(setModalOrderCustomer({ showPaymentMethod: true }))
	}, [dispatch])

	const closeReceiptPayerDataModal = useCallback(() => {
		dispatch(setModalOrderCustomer({ showReceiptPayerData: false }))
		dispatch(setSelectedOrder(null))
	}, [dispatch])

	const closeAbortOrderModal = useCallback(() => {
		dispatch(setModalOrderCustomer({ showAbortOrder: false }))
		dispatch(setModalOrderCustomer({ showOrder: true }))
	}, [dispatch])

	const handleAbortOrder = useCallback(async (reason, reasonCode) => {
		await dispatch(setOrderStatusThunk(orderData, 'ABORTED', reason, reasonCode))
		dispatch(setModalOrderCustomer({ showAbortOrder: false }))
	}, [dispatch, orderData])

	const closeCancelInvoiceOrderModal = useCallback(() => {
		dispatch(setModalOrderCustomer({ showCancelOrder: false }))
		dispatch(setModalOrderCustomer({ showOrder: true }))
	}, [dispatch])

	const handleCancelInvoiceOrder = useCallback(async (reason, reasonCode) => {
		await dispatch(setOrderStatusThunk(orderData, 'CANCELED', reason, reasonCode))
		dispatch(setModalOrderCustomer({ showCancelOrder: false }))
		dispatch(setModalOrderCustomer({ showOrder: false }))
	}, [dispatch, orderData])

	const closeChangePaymentConfirm = useCallback(() => {
		dispatch(setModalOrderCustomer({ showChangePaymentConfirm: false }))
		dispatch(setModalOrderCustomer({ showOrder: true }))
	}, [dispatch])

	const confirmChangePayment = useCallback(() => {
		dispatch(setModalOrderCustomer({ showChangePaymentConfirm: false }))
		dispatch(setDeliveryCartOrder(orderData))
		dispatch(setModalOrderCustomer({ showPaymentMethod: true }))
	}, [dispatch, orderData])

	return (
		<>
			<CustomerOrderButtonsModal />
			<SingleDriverModal />
			{!isInvoiceOrder(orderData) &&
				<CancelOrder />}
			<CancelInvoiceOrderModal isOpen={showCancelOrder && isInvoiceOrder(orderData)}
												close={closeCancelInvoiceOrderModal}
												onConfirm={handleCancelInvoiceOrder}
												order={orderData} />
			<ChangePaymentConfirmModal isOpen={showChangePaymentConfirm}
																		 close={closeChangePaymentConfirm}
																		 onConfirm={confirmChangePayment}
																		 order={orderData} />
			<AbortOrderModal isOpen={showAbortOrder}
											 close={closeAbortOrderModal}
											 onAbortOrder={handleAbortOrder} />
			<OrderDataModal order={orderData}
											modalIsOpen={showOrderData}
											close={closeModalOrderData} />
			<ModalPayment isOpen={showPaymentMethod}
										closeModal={closeModalPayment}
										openModal={reOpenPaymentModal}
										totalSum={isPaid ? totalSum : totalSumWithoutPrepaid}
										handlePayment={handlePayment}
										remark={deliveryCartOrder?.remark}
										isDeferredPayment={!isPaid}
										updatePaymentMode={isPaid}
										module={deliveryCartOrder?.module?.moduleType}
										orderType={deliveryCartOrder?.orderType}
										platformType={deliveryCartOrder?.platformMetaData?.platformType}
										resetCart={closeModalPayment}
										customerOrder={deliveryCartOrder?.customer}
										orderId={deliveryCartOrder?.orderId}
										changeCalculatorIsNeeded={!isPaid} />
			<PrintCanceledOrder />
			{showDriverConfirmationModal && <DriverConfirmationModal />}
			{showNoDriverModal && <NoDriverModal />}
			<PrintReceiptConfirmationModal />
			<PayerDataModal isOpen={showReceiptPayerData}
											close={closeReceiptPayerDataModal}
											order={orderData} />
		</>
	)
})

DeliveryModals.displayName = 'DeliveryModals'
export default DeliveryModals
